import { Popover } from '../components/FarChat/ChatFeedbackOverlays';
import React, { useEffect, useId, useState } from 'react';
import { Alert, Box, Button, IconButton, Link, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { ApproximateCostPlanPublic, ApproximateLocalCostEvidence, ApproximatePlanningWorkLine, ApproximateSourceAdjustment, ApproximateSourceLine } from '../api/approximateCostPlan';
import { OBSOLETE_MATERIAL_OBSERVATION_WARNING } from '../api/approximateCostPlan';
import type { ConstructionTimingRequirement, PublicPriceEvidencePublic } from '../api/constructionPriceEstimatorFunctions';
import { formatConstructionMoney } from './constructionPriceMoney';

const money = (value: unknown): string =>
  formatConstructionMoney(value, 'USD', 'Not estimated');
const isOneUnit = (quantity: string): boolean => /^(?:1(?:\.0+)?|one)\s+\S/i.test(quantity.trim());

// A missing or invalid amount is unpriced. A server-provided zero is still a
// numeric estimate (for example, an owner-entered cost with an explicit-zero
// reason), so the browser must not silently discard it.
const isPricedAmount = (value: string | number | null | undefined): boolean => (
  value != null && value !== '' && Number.isFinite(Number(value))
);

export const hasApproximateCostPlanPrices = (plan: ApproximateCostPlanPublic, awardEvidenceIsContextOnly = false): boolean => {
  const award = plan.award_reference;
  const awardHasPrice = !awardEvidenceIsContextOnly && Boolean(award?.compatible)
    && (award?.reference_kind === undefined || award.reference_kind === 'legacy_award_range'
      || award.reference_kind === 'conservative_comparable_award_point')
    && [award?.amount, award?.low, award?.high].some(isPricedAmount);
  // An empty calculation can aggregate to zero. An explicit zero-priced
  // contributor or compatible reference still counts as a displayed result.
  return (isPricedAmount(plan.priced_subtotal) && Number(plan.priced_subtotal) !== 0) || awardHasPrice
    || [...plan.labor_lines, ...plan.material_lines, ...plan.other_costs,
      ...(plan.installed_work_lines ?? []), ...(plan.site_role_lines ?? []),
      ...(plan.indirect_allowances ?? []),
      ...(plan.planning_work_lines ?? []).filter((line) => line.included_in_subtotal),
    ].some((line) => isPricedAmount(line.amount));
};

const publicEvidenceDate = (value: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return value;
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))));
};

const BTB_BENCHMARK_URL = 'https://www.energy.gov/eere/buildings/building-technologies-office';
const BTB_EXPLANATION = 'BTB is national benchmark data from the U.S. Department of Energy Building Technologies Office and the National Renewable Energy Laboratory. It is not a local quote.';
const DLA_PROCUREMENT_HISTORY_LABEL = 'Defense Logistics Agency procurement history';
const DLA_USED_COMPONENT_DETAIL = 'Used once for an exact material component. This national historical source supports materials only, not installed work, labor, freight, or tax.';
const subtotalContributorEstimateStyle = { paddingLeft: 22 };
const subtotalItemEstimateStyle = { paddingLeft: 30 };
const AWARD_RANGE_SCOPE_DISCLOSURE = 'This is a wide range across related federal projects. Their scopes may differ from this project.';
const LEGACY_G_AND_A_BASIS = 'Planning allowance on eligible materials, verified WD labor, labor employment support, and separately priced direct project costs.';
const conciseGAndABasis = (basis: string): string => basis.startsWith(LEGACY_G_AND_A_BASIS)
  ? 'Applied to priced labor, labor support, materials, and direct project costs.' : basis;
const listWithAnd = (items: string[]): string => items.length <= 2 ? items.join(' and ')
  : `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
const displaySourceDetail = (value: string): string => value.replace(/\bpartial\b/gi, 'limited');
const SITE_WAGE_COMPARISON = /^Site-rol(?:e)? wage(?:s| share) (?:meet or exceed|exceed|cannot be compared|is provisional)\b/i;
const SITE_WAGE_EXCEEDS_CRAFT_WARNING = 'Site-role wages exceed priced direct craft labor. Review staffing days, headcount, shared people, and missing craft components; the amounts are not capped.';

// These are useful limitations, but they are not action-blocking failures.
// Keep the compact ROM focused on its priced rows rather than repeating them
// as generic warning banners. Exact matching deliberately leaves new server
// warnings visible until they receive an intentional owner-facing treatment.
const ROM_DISCLOSURE_WARNINGS = new Set([
  OBSOLETE_MATERIAL_OBSERVATION_WARNING,
  'Catalog/material geography is not a local availability or delivery confirmation.',
  'Labor craft mapping is provisional.',
  'Material price is a historical Defense Logistics Agency procurement observation, not a current local quote.',
  'Required other costs are not estimated.',
  'Wall finish labor is not estimated because an explicit, verified Painter Wage Determination wage plus fringe is unavailable.',
]);

// Older and current server revisions retain missing-work diagnostics for
// audit. They must not reappear as owner-facing ROM banners now that the
// table intentionally shows priced items only. Keep source/price limitations
// that qualify a displayed estimate (rather than describing a missing item).
const describesUnestimatedWork = (message: string): boolean => (
  /\bnot\s+(?:yet\s+)?(?:estimated|priced)\b|\bunestimated\b|\bunpriced\b|\bno\s+matching\s+wage\s+determination\s+rate\b|\bwage\s+determination\s+labor\s+rates\s+are\s+unavailable\s+for\s+this\s+estimate\b|\bno\s+exact\s+compatible\s+primer\s+identity,\s+coverage,\s+and\s+price\s+are\s+active\b|\bno\s+approved\s+quantity\s+and\s+price\s+evidence\s+is\s+active\b/i.test(message)
);

// The server ledger identifies source families; make the actual source clear
// to an owner while accepting the labels persisted by earlier revisions.
const isBtbSourceLabel = (label: string): boolean => {
  const normalized = label.trim().toLowerCase();
  return /(^|[^a-z])btb([^a-z]|$)|national technology cost benchmark|national planning baseline|national benchmark\s*[·-]\s*\d{4}\s*dollars|buildings technology baseline/.test(normalized);
};

const sourceLedgerLabel = (label: string): string => {
  if (label === 'Previous federal awards' || label === 'Previous federal awards (USAspending)') return 'USAspending award totals';
  if (isBtbSourceLabel(label)) return 'National technology cost benchmark — BTB';
  return label;
};

const isDlaProcurementHistory = (label: string): boolean => (
  sourceLedgerLabel(label) === DLA_PROCUREMENT_HISTORY_LABEL
);

// A checked source is not necessarily a price source. Failed, unavailable,
// context-only, and no-match checks remain in the saved audit ledger but do
// not belong in a detail popup for a displayed estimate.
const isUsedSource = (source: { outcome?: string; contribution?: string }): boolean => (
  (source.outcome == null || source.outcome === 'consulted' || source.outcome === 'reused')
  && (source.contribution == null || source.contribution === 'used')
);

const localEvidenceLabel = (evidence: ApproximateLocalCostEvidence): string => {
  switch (evidence.source_label) {
    case 'Defense Logistics Agency procurement history': return 'Defense Logistics Agency procurement history';
    case 'GSA Global Supply facility-material catalog': return 'GSA Global Supply material catalog snapshot';
    case 'Georgia Department of Transportation bid tabs': return 'Georgia Department of Transportation installed-civil bid-tab evidence';
    case 'U.S. Geological Survey mineral and aggregate releases': return 'U.S. Geological Survey mineral and aggregate context';
  }
};

const localEvidenceContribution = (evidence: ApproximateLocalCostEvidence): string => {
  if (evidence.evidence_kind === 'commodity context') return 'Context only; not used in this price.';
  if (evidence.contribution === 'checked_no_match') return 'No matching evidence; not used in this price.';
  if (evidence.source_label === DLA_PROCUREMENT_HISTORY_LABEL && evidence.contribution === 'used') return DLA_USED_COMPONENT_DETAIL;
  if (evidence.evidence_kind === 'all-in installed civil work') return 'Used as all-in installed work; it is not added to separate labor or material amounts.';
  return 'Used only for an exact mapped material component; it is not an installed-work price.';
};

type SourceCheck = { label: string; detail?: string; status: string; url?: string; target: string; evidenceCount?: number; asOf?: string };
const sourceCheckStatus = (outcome?: string): string => {
  if (outcome === 'reused') return 'Saved result reused; used for this estimate.';
  return 'Used for this estimate.';
};
const sourceFamily = (label: string): string | null => {
  const value = label.toLowerCase();
  if (/gsa global supply|gsa facility-material catalog/.test(value)) return 'gsa_global_supply';
  if (/gsa mas paint-accessory|gsa multiple award schedule price list/.test(value)) return 'gsa_mas_accessory';
  if (/georgia statewide paint-accessory|georgia statewide contract price export/.test(value)) return 'georgia_accessory';
  if (/reviewed retailer paint-accessory|reviewed public retailer observation/.test(value)) return 'retailer_accessory';
  if (/gsa short-term rental ceiling/.test(value)) return 'gsa_rental';
  if (/gsa sanitation price schedule|gsa rockwell sanitation/.test(value)) return 'gsa_sanitation';
  if (/gsa dumpster price schedule|gsa amn dumpster/.test(value)) return 'gsa_dumpster';
  if (/gsa roofing work schedule|gsa hound roofing/.test(value)) return 'gsa_roofing';
  if (/gd[o]?t bid.tab|georgia department of transportation.*bid.tab/.test(value)) return 'gdot';
  if (/usgs mineral|geological survey mineral/.test(value)) return 'usgs';
  if (/\bdla\b|defense logistics agency/.test(value)) return 'dla';
  if (isBtbSourceLabel(value)) return 'btb';
  return null;
};
const sourceLabelsRelated = (left: string, right: string): boolean => {
  const a = left.toLowerCase().trim();
  const b = right.toLowerCase().trim();
  const family = sourceFamily(a);
  return Boolean(a && b && (a.includes(b) || b.includes(a)
    || (family && family === sourceFamily(b))));
};
const sourceTarget = (
  label: string,
  plan: ApproximateCostPlanPublic,
  laborGroups: LaborLineGroup[],
  pricedMaterials: ApproximateCostPlanPublic['material_lines'],
  planningLines: ApproximatePlanningWorkLine[],
  installedWorkLines: NonNullable<ApproximateCostPlanPublic['installed_work_lines']>,
  awardHasPrice: boolean,
): string[] => {
  const normalized = label.toLowerCase();
  if (/usaspending|previous federal awards|historical federal awards|local awarded project totals/.test(normalized)) return awardHasPrice ? ['award'] : [];
  if (/wage determination|\bsam\.gov\b/.test(normalized)) return laborGroups.map((group) => `labor-${group.label}`);
  if (/occupational employment|\boews\b/.test(normalized)) {
    const pricedRoles = (plan.site_role_lines ?? []).filter((line) => isPricedAmount(line.amount));
    return pricedRoles.map((line) => `site-${line.role_key}`);
  }
  if (/bid.tab|installed civil/.test(normalized)) {
    if (installedWorkLines.some((line) => sourceLabelsRelated(label, line.source_label))) return ['installed-work'];
    return planningLines.filter((line) => line.category === 'installed_work' && sourceLabelsRelated(label, line.source_label)).map((line) => line.occurrence_id);
  }
  const adjustmentKind = /producer price index/.test(normalized) ? 'ppi_escalation'
    : /usace pax facility cost adjustment/.test(normalized) ? 'pax_area_time' : null;
  if (adjustmentKind) {
    const adjustedLineIds = new Set((plan.source_adjustments ?? []).filter((row) => row.kind === adjustmentKind).map((row) => row.line_id));
    const materialTargets = pricedMaterials.flatMap((line, index) => line.line_id && adjustedLineIds.has(line.line_id) ? [`material-${index}`] : []);
    const laborTargets = laborGroups.flatMap((group) => group.lines.some((line) => line.line_id && adjustedLineIds.has(line.line_id)) ? [`labor-${group.label}`] : []);
    return materialTargets.concat(laborTargets);
  }
  const matchingMaterials = pricedMaterials.flatMap((line, index) => sourceLabelsRelated(label, line.source_label) ? [`material-${index}`] : []);
  if (matchingMaterials.length) return matchingMaterials;
  const matchingPlanningLines = planningLines.filter((line) => sourceLabelsRelated(label, line.source_label));
  if (matchingPlanningLines.length) return matchingPlanningLines.map((line) => line.occurrence_id);
  // Family-level checks without a SKU or priced line attribution stay in the
  // audit ledger. The priced line already names its actual price source.
  // Technical data can supply a quantity/coverage assumption rather than the
  // unit price. Only associate it when that same citation occurs in the saved
  // priced plan and there is a single unambiguous material line.
  const normalizedLabel = normalized.replace(/[^a-z0-9]+/g, ' ').trim();
  if (/technical data/.test(normalizedLabel) && pricedMaterials.length === 1
    && (plan.assumptions ?? []).some((assumption) => assumption.toLowerCase().replace(/[^a-z0-9]+/g, ' ').includes(normalizedLabel))) {
    return ['material-0'];
  }
  return [];
};

const SourceChecks: React.FC<{ checks: SourceCheck[]; alreadyShownSources?: string[] }> = ({ checks, alreadyShownSources = [] }) => checks.length ? <Box sx={{ mt: 1 }}>
  {checks.map((check, index) => <Box key={`${check.label}-${index}`} sx={{ mt: index ? 0.75 : 0 }}>
    {!alreadyShownSources.some((label) => sourceLabelsRelated(label, check.label)) && <Typography variant="body2" fontWeight={600}>{check.label}</Typography>}
    {check.status !== 'Checked.' && <Typography variant="body2" color="text.secondary">{check.status}</Typography>}
    {(check.evidenceCount !== undefined || check.asOf) && <Typography variant="body2" color="text.secondary">
      {[check.evidenceCount !== undefined ? `${check.evidenceCount} ${check.evidenceCount === 1 ? 'result' : 'results'}` : null,
        check.asOf ? `Source date: ${publicEvidenceDate(check.asOf)}` : null].filter(Boolean).join(' · ')}
    </Typography>}
    {check.detail && <Typography variant="body2" color="text.secondary">{displaySourceDetail(check.detail)}</Typography>}
    {check.url && <Link href={check.url} target="_blank" rel="noopener noreferrer">About this source</Link>}
  </Box>)}
</Box> : null;

const sameSourceUrl = (left: string, right: string): boolean => {
  try { return new URL(left).href === new URL(right).href; } catch { return left === right; }
};

const Source: React.FC<{ line: ApproximateSourceLine; adjustment?: ApproximateSourceAdjustment; primaryUrl?: string | null }> = ({ line, adjustment, primaryUrl }) => {
  const sourceUrl = primaryUrl || line.source_url || adjustment?.source_url;
  const distinctAdjustmentUrl = adjustment?.source_url && sourceUrl && !sameSourceUrl(adjustment.source_url, sourceUrl)
    ? adjustment.source_url : null;
  const wageDeterminationOnly = Boolean(primaryUrl && sourceUrl
    && (!line.source_url || sameSourceUrl(line.source_url, sourceUrl))
    && (!adjustment?.source_url || sameSourceUrl(adjustment.source_url, sourceUrl)));
  const hasAdjustmentMath = adjustment && (
    Number(adjustment.factor) !== 1 || Number(adjustment.input_amount) !== Number(adjustment.output_amount)
  );
  return <>
    {(line.source_label || line.source_date) && <Typography variant="body2" color="text.secondary">{[line.source_label, line.source_date && publicEvidenceDate(line.source_date)].filter(Boolean).join(' · ')}</Typography>}
    {hasAdjustmentMath && <Typography variant="body2" sx={{ mt: 1 }}>{adjustment.source_label}: {adjustment.factor} × {money(adjustment.input_amount)} = {money(adjustment.output_amount)}</Typography>}
    {distinctAdjustmentUrl && <Link href={distinctAdjustmentUrl} target="_blank" rel="noopener noreferrer">View adjustment source</Link>}
    {sourceUrl && <Box sx={{ mt: 1 }}><Link href={sourceUrl} target="_blank" rel="noopener noreferrer">{wageDeterminationOnly ? 'View Wage Determination' : 'View source'}</Link></Box>}
  </>;
};

const InstalledWorkDetails: React.FC<{ lines: NonNullable<ApproximateCostPlanPublic['installed_work_lines']> }> = ({ lines }) => <>
  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Selected installed-work evidence</Typography>
  <Typography sx={{ mb: 1 }}>These awarded all-in installed-work comparables replace separate labor, materials, and other recipe rows. They are not added to those costs.</Typography>
  {lines.map((line) => <Box key={line.work_package_id} sx={{ mb: 1.5 }}>
    <Typography fontWeight={700}>{line.label}</Typography>
    <Typography>{line.evidence_label}</Typography>
    <Typography>Matched quantity: {line.matched_quantity_label}</Typography>
    <Typography variant="body2" color="text.secondary">Source: {line.source_label}</Typography>
    <Typography variant="body2" color="text.secondary">{publicEvidenceDate(line.source_date)}</Typography>
    <Typography variant="body2" color="text.secondary">{line.geography_label}</Typography>
    <Typography variant="body2" color="text.secondary">Georgia nonlocal planning comparable</Typography>
    {!describesUnestimatedWork(line.limitation) && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{displaySourceDetail(line.limitation)}</Typography>}
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}><Link href={line.source_url} target="_blank" rel="noopener noreferrer">View public source</Link></Typography>
  </Box>)}
</>;

/** A click-only, keyboard-accessible disclosure for calculation provenance. */
const LineItemDetails: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);
  return <>
    <IconButton size="small" aria-label={`View details for ${label}`} onClick={(event) => setAnchor(event.currentTarget)}>
      <InfoOutlinedIcon fontSize="small" />
    </IconButton>
    <Popover
      open={open}
      anchorEl={anchor}
      onClose={() => setAnchor(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{ role: 'dialog', 'aria-label': `Details for ${label}`, sx: { p: 2, maxWidth: 480 } }}
    >
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={700}>{label}</Typography>
          <Button size="small" onClick={() => setAnchor(null)} aria-label={`Close details for ${label}`}>Close</Button>
        </Box>
        {children}
      </Box>
    </Popover>
  </>;
};

const timingUnitLabels = {
  calendar_days: 'calendar days', workdays: 'workdays', unspecified_days: 'days (day type not stated)',
  weeks: 'weeks', months: 'months', hours: 'hours', years: 'years',
};

/** Stated contract timing is evidence, never a forecast derived from craft hours. */
const StatedProjectTiming: React.FC<{
  items: ConstructionTimingRequirement[];
  sourceStatus?: 'complete' | 'unreadable' | 'not_available';
  stale: boolean;
}> = ({ items, sourceStatus, stale }) => {
  const overall = items.filter(item => item.kind === 'overall_performance_period');
  return <Box component="section" aria-label="Stated project timing" sx={{ mt: 0.5 }}>
    {overall.length === 0 && <Typography variant="body2">Project completion requirement: {sourceStatus === 'unreadable' ? 'Unavailable' : 'Not stated'}.</Typography>}
    {overall.map((item, index) => {
      const label = item.strength === 'required' ? 'Required project completion'
        : item.strength === 'planning_target' ? 'Project planning target' : 'Stated project period';
      const value = item.value != null
        ? `${item.value}${item.value_max ? `–${item.value_max}` : ''} ${item.unit ? timingUnitLabels[item.unit] : '(unit not stated)'}`
        : item.end_date || item.label;
      return <Box key={item.id} sx={{ mt: index ? 0.5 : 0 }}>
        <Typography variant="body2">
          {stale ? `Saved ${label.charAt(0).toLowerCase()}${label.slice(1)}` : label}: {value}.
          {item.trigger ? ` Start trigger: ${item.trigger}.` : ' Start trigger: Not stated.'}
          {item.value != null && item.end_date ? ` Stated completion date: ${item.end_date}.` : ''}
          <LineItemDetails label={`Project timing ${index + 1}`}>
            <Typography variant="body2">{item.source_kind === 'project_brief' ? 'User-stated project description' : item.document_name || 'Statement of Work'}</Typography>
            <Typography variant="body2" color="text.secondary">{item.source_locator}</Typography>
            <Typography component="blockquote" variant="body2" sx={{ mx: 0, whiteSpace: 'pre-wrap' }}>{item.evidence_quote}</Typography>
            {item.start_date && <Typography variant="body2">Stated start: {item.start_date}</Typography>}
            {item.conditions.map((condition, conditionIndex) => <Typography key={conditionIndex} variant="body2">{condition}</Typography>)}
          </LineItemDetails>
        </Typography>
        {item.discrepancy && <Typography variant="body2" color="warning.main">{item.discrepancy}</Typography>}
        {item.owner_override?.description && <Typography variant="body2">User-edited project timing: {item.owner_override.description}</Typography>}
      </Box>;
    })}
    {stale && <Typography variant="body2" color="warning.main">Saved timing may be out of date. Fill contract details to refresh it.</Typography>}
    {sourceStatus === 'unreadable' && <Typography variant="body2" color="text.secondary">Timing extraction is incomplete. No duration was inferred.</Typography>}
  </Box>;
};

const LaborLineDetails: React.FC<{ line: ApproximateCostPlanPublic['labor_lines'][number]; adjustment?: ApproximateSourceAdjustment }> = ({ line, adjustment }) => <>
  <Typography variant="body2">{Number(line.hours) === 1
    ? `Hourly rate: ${money(line.base_wage)} wage + ${money(line.fringe)} fringe = ${money(line.amount)}`
    : `${line.hours} person-hours × (${money(line.base_wage)} wage + ${money(line.fringe)} fringe)/hour = ${money(line.amount)}`}</Typography>
  <Source line={line} adjustment={adjustment} primaryUrl={line.wage_determination_url} />
</>;

/** A craft may have several supported tasks; keep its total on one useful row. */
const LaborLineGroupDetails: React.FC<{ lines: ApproximateCostPlanPublic['labor_lines']; adjustmentByLineId: Map<string, ApproximateSourceAdjustment> }> = ({ lines, adjustmentByLineId }) => <>
  {lines.map((line, index) => <Box key={`${line.source_label}-${line.hours}-${index}`} sx={{ mb: index + 1 < lines.length ? 2 : 0 }}>
    {lines.length > 1 && <Typography variant="subtitle2" fontWeight={700}>Labor task {index + 1}</Typography>}
    <LaborLineDetails line={line} adjustment={line.line_id ? adjustmentByLineId.get(line.line_id) : undefined} />
  </Box>)}
</>;

const MaterialLineDetails: React.FC<{ line: ApproximateCostPlanPublic['material_lines'][number]; adjustment?: ApproximateSourceAdjustment }> = ({ line, adjustment }) => {
  const quantity = line.purchased_quantity_label.split(';')[0].trim();
  const onePackage = isOneUnit(quantity);
  return <>
    <Typography variant="body2">{onePackage
      ? Number(line.unit_price) === Number(line.amount) ? `Price: ${money(line.amount)}` : `Unit price: ${money(line.unit_price)}; total: ${money(line.amount)}`
      : `${quantity} × ${money(line.unit_price)} per package = ${money(line.amount)}`}</Typography>
    <Source line={line} adjustment={adjustment} />
  </>;
};

const IndirectAllowanceDetails: React.FC<{ allowance: NonNullable<ApproximateCostPlanPublic['indirect_allowances']>[number] }> = ({ allowance }) => {
  const basisLines = allowance.basis_lines ?? [];
  const percentage = `${Number(allowance.percentage) * 100}%`;
  return <>
    {basisLines.map((line, index) => <Typography variant="body2" key={`${line.craft_label}-${index}`}>
      {line.craft_label}: {Number(line.hours).toLocaleString('en-US')} hours × {money(line.base_wage)} = {money(line.basic_wages)}
    </Typography>)}
    {basisLines.length > 0 && <Typography variant="body2" sx={{ mt: 1 }}>
      Basic-wage base: {basisLines.map((line) => money(line.basic_wages)).join(' + ')} = {money(allowance.base_amount)}
    </Typography>}
    <Typography variant="body2">
      {basisLines.length
        ? `${percentage} × ${money(allowance.base_amount)} = ${money(allowance.amount)}`
        : `${percentage} of verified Wage Determination basic wages: ${money(allowance.base_amount)} × ${percentage} = ${money(allowance.amount)}`}
    </Typography>
  </>;
};

/**
 * Older saved revisions named every craft line "Direct labor." Keep those
 * revisions intelligible without mutating their saved evidence: use their
 * existing craft field as the displayed row name. Newer projections also use
 * this name directly.
 */
const visibleLaborLineLabel = (line: ApproximateCostPlanPublic['labor_lines'][number]): string => (
  line.label.trim().toLowerCase() === 'direct labor' && line.craft_label.trim()
    ? line.craft_label
    : line.label
);

type LaborLineGroup = {
  label: string;
  lines: ApproximateCostPlanPublic['labor_lines'];
  amount: number;
};

const groupLaborLines = (lines: ApproximateCostPlanPublic['labor_lines']): LaborLineGroup[] => {
  const groups = new Map<string, LaborLineGroup>();
  lines.forEach((line) => {
    const label = visibleLaborLineLabel(line);
    const existing = groups.get(label);
    const amount = Number(line.amount);
    if (existing) {
      existing.lines.push(line);
      existing.amount += amount;
    } else {
      groups.set(label, { label, lines: [line], amount });
    }
  });
  return Array.from(groups.values());
};

/**
 * Compact rows inside the Labor and Materials disclosures deliberately show
 * only the item and its extended price. Arithmetic and source provenance stay
 * behind the explicit, click-only details control for that item.
 */
const ComponentLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography component="span" variant="body2">{children}</Typography>
);
const BasisCell: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <TableCell>{children}</TableCell>
);

const CompactLineItem: React.FC<{
  label: string;
  amount: string | number | null | undefined;
  children: React.ReactNode;
}> = ({ label, amount, children }) => <TableRow
  sx={{
    bgcolor: 'action.hover',
    '& .MuiTableCell-root': { py: 0.5 },
  }}
>
  <TableCell component="th" scope="row" className="rom-nested-label"><ComponentLabel>{label}</ComponentLabel></TableCell>
  <TableCell className="rom-estimate-cell rom-subtotal-item" style={subtotalItemEstimateStyle} sx={{ fontWeight: 400 }}>{money(amount)}</TableCell>
  <TableCell><LineItemDetails label={label}>{children}</LineItemDetails></TableCell>
</TableRow>;

const CompactLineItems: React.FC<{
  kind: 'labor' | 'materials';
  laborLines?: ApproximateCostPlanPublic['labor_lines'];
  siteRoleLines?: ApproximateCostPlanPublic['site_role_lines'];
  materialLines?: ApproximateCostPlanPublic['material_lines'];
  indirectAllowances?: ApproximateCostPlanPublic['indirect_allowances'];
  sourceAdjustments?: ApproximateCostPlanPublic['source_adjustments'];
  sourceChecksFor?: (target: string) => SourceCheck[];
}> = ({ kind, laborLines = [], siteRoleLines = [], materialLines = [], indirectAllowances = [], sourceAdjustments = [], sourceChecksFor = () => [] }) => {
  const adjustmentByLineId = new Map(sourceAdjustments.map((adjustment) => [adjustment.line_id, adjustment]));
  if (kind === 'labor') {
    const groups = groupLaborLines(laborLines);
    const employmentSupport = indirectAllowances.find((allowance) => allowance.kind === 'labor_employment_support' && isPricedAmount(allowance.amount));
    return <>
      {groups.map((group) => <CompactLineItem key={`labor-${group.label}`} label={group.label} amount={group.amount}><LaborLineGroupDetails lines={group.lines} adjustmentByLineId={adjustmentByLineId} /><SourceChecks checks={sourceChecksFor(`labor-${group.label}`)} alreadyShownSources={group.lines.map((line) => line.source_label)} /></CompactLineItem>)}
      {siteRoleLines.map((line) => <CompactLineItem key={`site-${line.role_key}`} label={line.label} amount={line.amount}>
        <Typography variant="body2">{Number(line.hours) === 1
          ? `Hourly wage: ${money(line.hourly_wage)}`
          : `${line.hours} person-hours × ${money(line.hourly_wage)}/hour = ${money(line.amount)}`}</Typography>
        <Typography variant="body2" color="text.secondary">{line.source_label} · {line.release_year}</Typography>
        {line.source_url && <Link href={line.source_url} target="_blank" rel="noopener noreferrer">View public source</Link>}
        <SourceChecks checks={sourceChecksFor(`site-${line.role_key}`)} alreadyShownSources={[line.source_label]} />
      </CompactLineItem>)}
      {employmentSupport && <CompactLineItem label={employmentSupport.label} amount={employmentSupport.amount}><IndirectAllowanceDetails allowance={employmentSupport} /><SourceChecks checks={sourceChecksFor('labor-support')} /></CompactLineItem>}
    </>;
  }
  return <>
    {materialLines.map((line, index) => <CompactLineItem key={`material-${index}`} label={line.label} amount={line.amount}><MaterialLineDetails line={line} adjustment={line.line_id ? adjustmentByLineId.get(line.line_id) : undefined} /><SourceChecks checks={sourceChecksFor(`material-${index}`)} alreadyShownSources={[line.source_label]} /></CompactLineItem>)}
  </>;
};

const planningPriceLabel: Record<ApproximatePlanningWorkLine['price_kind'], string> = {
  published_benchmark: 'Published benchmark',
  adjusted_proxy: 'Adjusted planning proxy',
  material_only_component: 'Material-only component',
  installed_work: 'Installed work',
};

const PlanningWorkLineRow: React.FC<{ line: ApproximatePlanningWorkLine; nested?: boolean; sourceChecks?: SourceCheck[] }> = ({ line, nested = false, sourceChecks = [] }) => <TableRow sx={nested ? { bgcolor: 'action.hover', '& .MuiTableCell-root': { py: 0.5 } } : undefined}>
  <TableCell component="th" scope="row" className={nested ? 'rom-nested-label' : undefined}><ComponentLabel>{line.category === 'installed_work' ? `All-in installed work — ${line.label}` : line.label}</ComponentLabel></TableCell>
  <TableCell className={`rom-estimate-cell ${nested ? 'rom-subtotal-item' : 'rom-subtotal-contributor'}`} style={nested ? subtotalItemEstimateStyle : subtotalContributorEstimateStyle}>{money(line.amount)}</TableCell>
  <BasisCell>
    <Typography component="span" variant="body2">{planningPriceLabel[line.price_kind]}</Typography>
    <LineItemDetails label={line.label}>
      {line.roof_insulation_details ? <>
        <Typography variant="body2">Target R-{line.roof_insulation_details.target_r} · Achieved R-{line.roof_insulation_details.achieved_r} · {line.roof_insulation_details.total_thickness_inches} inches total</Typography>
        {line.roof_insulation_details.layers.map((layer, index) => <Typography key={index} variant="body2">{layer.label}: {layer.quantity} boards × {money(layer.unit_price)} = {money(layer.amount)} · R-{layer.r_value} · {layer.coverage_sf} sq ft/board</Typography>)}
        <Typography variant="body2" color="text.secondary">Supplier-stated roof-board planning configuration. Product and roof-system compatibility should be checked for the project.</Typography>
      </> : <Typography variant="body2">{isOneUnit(line.quantity_label)
        ? `Price: ${line.unit_price_label}`
        : `${line.quantity_label} × ${line.unit_price_label} = ${money(line.amount)}`}</Typography>}
      <Typography variant="body2" color="text.secondary">{[line.source_label, line.source_date && publicEvidenceDate(line.source_date)].filter(Boolean).join(' · ')}</Typography>
      {line.source_url && <Link href={line.source_url} target="_blank" rel="noopener noreferrer">View public source</Link>}
      {line.roof_insulation_details?.specification_source_url && line.roof_insulation_details.specification_source_url !== line.source_url && <Link sx={{ display: 'block' }} href={line.roof_insulation_details.specification_source_url} target="_blank" rel="noopener noreferrer">View insulation specification</Link>}
      <SourceChecks checks={sourceChecks} alreadyShownSources={[line.source_label]} />
    </LineItemDetails>
  </BasisCell>
</TableRow>;

const UnpricedExplicitRow: React.FC<{ row: NonNullable<ApproximateCostPlanPublic['unpriced_explicit_components']>[number]; nested?: boolean }> = ({ row, nested = false }) => <TableRow sx={nested ? { bgcolor: 'action.hover', '& .MuiTableCell-root': { py: 0.5 } } : undefined}>
  <TableCell component="th" scope="row" className={nested ? 'rom-nested-label' : undefined}><ComponentLabel>{row.label}</ComponentLabel></TableCell>
  <TableCell className="rom-estimate-cell">Not estimated</TableCell>
  <BasisCell>{row.reason}</BasisCell>
</TableRow>;

const UnpricedSiteRoleRow: React.FC<{ row: NonNullable<ApproximateCostPlanPublic['unpriced_site_roles']>[number] }> = ({ row }) => <TableRow sx={{ bgcolor: 'action.hover', '& .MuiTableCell-root': { py: 0.5 } }}>
  <TableCell component="th" scope="row" className="rom-nested-label"><ComponentLabel>{row.label}</ComponentLabel></TableCell>
  <TableCell className="rom-estimate-cell">Not estimated</TableCell>
  <BasisCell>{row.reason}</BasisCell>
</TableRow>;

export default function ApproximateCostPlan({
  plan,
  awardEvidence,
  supportingEvidence = [],
  sourcesChecked = [],
  wageRefreshFailed = false,
  awardReferenceUnavailableReason,
  awardEvidenceIsContextOnly = false,
  timingRequirements = [],
  timingSourceStatus,
  timingIsStale = false,
}: {
  plan: ApproximateCostPlanPublic;
  awardEvidence?: React.ReactNode;
  wageDeterminationEvidence?: React.ReactNode;
  supportingEvidence?: Array<{ id: string; label: string; content: React.ReactNode }>;
  /** Reviewed whole-award context only; never cost-line input. */
  publicPriceEvidence?: PublicPriceEvidencePublic | null;
  /** Server-curated sources actually checked or reused by this saved result. */
  sourcesChecked?: Array<{ label: string; detail?: string; outcome?: string; contribution?: string; evidence_count?: number; as_of?: string }>;
  /** A failed fresh lookup retained an independently compatible saved schedule. */
  wageRefreshFailed?: boolean;
  /** Safe status copy for a completed-but-unavailable award source. */
  awardReferenceUnavailableReason?: string;
  /** Award rows are retained for review but cannot support a planning total. */
  awardEvidenceIsContextOnly?: boolean;
  timingRequirements?: ConstructionTimingRequirement[];
  timingSourceStatus?: 'complete' | 'unreadable' | 'not_available';
  timingIsStale?: boolean;
}) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const instanceId = useId();
  // The parent may normalize the same public plan into a new object on render.
  const planContentKey = JSON.stringify(plan);
  useEffect(() => setExpanded({}), [planContentKey]);
  const toggle = (id: string) => setExpanded(old => ({ ...old, [id]: !old[id] }));
  const regionId = (id: string): string => `${instanceId}-${id}`;
  const detail = (id: string, children: React.ReactNode, label = id) =>
    <TableRow sx={{ display: expanded[id] ? undefined : 'none' }}>
      <TableCell colSpan={3} sx={{ bgcolor: 'action.hover' }}>
        <Box id={regionId(id)} role="region" aria-label={label}>
          {expanded[id] ? children : null}
        </Box>
      </TableCell>
    </TableRow>;
  const expander = (id: string, label: string, accessible: string) =>
    <Button size="small" onClick={() => toggle(id)} aria-expanded={!!expanded[id]}
      aria-controls={id.startsWith('support-') || id === 'awards' || id === 'installed-work' ? regionId(id) : undefined} aria-label={accessible}
      sx={{ textTransform: 'none', justifyContent: 'flex-start', textAlign: 'left', fontSize: '0.875rem' }}>
      {expanded[id] ? '−' : '+'} <span>{label}</span>
    </Button>;
  const installedWorkLines = (plan.installed_work_lines ?? []).filter((line) => isPricedAmount(line.amount));
  const planningWorkLines = (plan.planning_work_lines ?? []).filter((line) => line.included_in_subtotal && isPricedAmount(line.amount));
  // All-in work is a replacement path, never an additive material line.
  // Prefer the explicit installed-work projection if a saved revision carries
  // both shapes, rather than presenting the same award as two components.
  const hasInstalledWork = installedWorkLines.length > 0;
  const planningDirectMaterialLines = planningWorkLines.filter((line) => line.display_group === 'direct_materials' || (line.display_group == null && line.category === 'materials'));
  const planningIndirectMaterialLines = planningWorkLines.filter((line) => line.display_group === 'indirect_materials'
    || (line.display_group == null && (line.category === 'equipment' || line.category === 'project_other')));
  // An all-in installed-work observation cannot honestly be split into labor
  // and materials. Keep it separate rather than inventing a material amount.
  const planningOtherLines = planningWorkLines.filter((line) => line.category === 'installed_work');
  const pricedWorkIds = new Set(planningWorkLines.map((line) => line.occurrence_id));
  const unpricedExplicit = (plan.unpriced_explicit_components ?? []).filter((row) => !pricedWorkIds.has(row.occurrence_id));
  const unpricedDirectMaterialComponents = unpricedExplicit.filter((row) => row.display_group === 'direct_materials' || (row.display_group == null && row.category === 'materials'));
  const unpricedIndirectMaterialComponents = unpricedExplicit.filter((row) => row.display_group === 'indirect_materials'
    || (row.display_group == null && row.category === 'project_other'));
  // The server keeps legacy `materials_total` limited to legacy material
  // lines. Published planning components are additive to the modeled
  // subtotal and must be additive in the visible Materials parent row too.
  const planningDirectMaterialTotal = planningDirectMaterialLines.reduce((sum, line) => sum + Number(line.amount), 0);
  const planningIndirectMaterialTotal = planningIndirectMaterialLines.reduce((sum, line) => sum + Number(line.amount), 0);
  const displayedDirectMaterialsTotal = planningDirectMaterialLines.length > 0
    ? Number(plan.materials_total ?? 0) + planningDirectMaterialTotal
    : plan.materials_total;
  // Only sourced or user-entered other costs belong in the compact ROM.
  // Unestimated scope and generic source limitations remain stored on the
  // result but are intentionally not restated in this table.
  const pricedOtherCosts = plan.other_costs.filter((row) => isPricedAmount(row.amount));
  const indirectAllowances = plan.indirect_allowances ?? [];
  const generalAndAdministrative = indirectAllowances.find((allowance) => allowance.kind === 'general_and_administrative' && isPricedAmount(allowance.amount));
  const pricedLaborLines = plan.labor_lines.filter((line) => isPricedAmount(line.amount));
  const pricedMaterialLines = plan.material_lines.filter((line) => isPricedAmount(line.amount));
  const hasPricedEmploymentSupport = indirectAllowances.some((allowance) => allowance.kind === 'labor_employment_support' && isPricedAmount(allowance.amount));
  const pricedSiteRoleLines = (plan.site_role_lines ?? []).filter((line) => isPricedAmount(line.amount));
  const pricedSiteRoleKeys = new Set(pricedSiteRoleLines.map((line) => line.role_key));
  const unpricedSiteRoles = (plan.unpriced_site_roles ?? []).filter((row) => !pricedSiteRoleKeys.has(row.role_key));
  const showLabor = (isPricedAmount(plan.labor_total) && (pricedLaborLines.length > 0 || pricedSiteRoleLines.length > 0 || hasPricedEmploymentSupport)) || unpricedSiteRoles.length > 0;
  const hasPricedDirectMaterials = pricedMaterialLines.length > 0 || planningDirectMaterialLines.length > 0;
  const showDirectMaterials = (isPricedAmount(displayedDirectMaterialsTotal) && hasPricedDirectMaterials) || unpricedDirectMaterialComponents.length > 0;
  const showIndirectMaterials = planningIndirectMaterialLines.length > 0 || unpricedIndirectMaterialComponents.length > 0;
  const awardReference = plan.award_reference;
  const awardHasPrice = !awardEvidenceIsContextOnly && Boolean(awardReference?.compatible)
    && (awardReference?.reference_kind === undefined
      || awardReference?.reference_kind === 'legacy_award_range'
      || awardReference?.reference_kind === 'conservative_comparable_award_point')
    && [awardReference?.amount, awardReference?.low, awardReference?.high].some(isPricedAmount);
  // Keep only source facts that contributed to a displayed price. An active
  // snapshot or a completed lookup alone is not price attribution.
  const actualDlaMaterialUse = (plan.material_component_replacements ?? []).some((item) => item.source_label === DLA_PROCUREMENT_HISTORY_LABEL)
    || pricedMaterialLines.some((line) => /(?:^|\b)(?:DLA|Defense Logistics Agency)(?:\b|$)/i.test(line.source_label));
  // BTB's lane-level ledger is context even when a priced line explicitly
  // cites the benchmark. Do not extend that override to other context-only
  // snapshots, which can be unrelated to a similarly named catalog line.
  const directlyCited = (label: string): boolean => pricedMaterialLines.some((line) => sourceLabelsRelated(label, line.source_label))
    || planningWorkLines.some((line) => sourceLabelsRelated(label, line.source_label))
    || installedWorkLines.some((line) => sourceLabelsRelated(label, line.source_label));
  const contextCheckPricesDisplayedItem = (source: typeof sourcesChecked[number]): boolean => (
    source.contribution === 'context'
    && (source.outcome == null || source.outcome === 'consulted' || source.outcome === 'reused')
    && ((isBtbSourceLabel(source.label) && directlyCited(source.label))
      || (awardHasPrice && /usaspending|previous federal awards|historical federal awards/i.test(source.label)))
  );
  const visibleSourcesChecked = sourcesChecked.filter((source) => (isUsedSource(source) || contextCheckPricesDisplayedItem(source))
    && (!isDlaProcurementHistory(source.label) || actualDlaMaterialUse));
  const localChecks = (plan.cost_evidence ?? []).filter((evidence) => (
    evidence.contribution === 'used'
    &&
    (evidence.source_label !== DLA_PROCUREMENT_HISTORY_LABEL || actualDlaMaterialUse)
    && !visibleSourcesChecked.some((source) => sourceLabelsRelated(sourceLedgerLabel(source.label), localEvidenceLabel(evidence)))
  ));
  if (actualDlaMaterialUse && !visibleSourcesChecked.some((source) => isDlaProcurementHistory(source.label))
    && !localChecks.some((evidence) => evidence.source_label === DLA_PROCUREMENT_HISTORY_LABEL)) {
    localChecks.push({ source_label: DLA_PROCUREMENT_HISTORY_LABEL, evidence_kind: 'historical material observation', contribution: 'used' });
  }
  const laborGroups = groupLaborLines(pricedLaborLines);
  const sourceFacts = [
    ...visibleSourcesChecked.map((source) => {
      const relatedLocal = (plan.cost_evidence ?? []).find((evidence) => evidence.contribution === 'used'
        && sourceLabelsRelated(sourceLedgerLabel(source.label), localEvidenceLabel(evidence)));
      const btbDetail = source.contribution === 'context' && /context|not used|no compatible/i.test(source.detail ?? '')
        ? undefined : source.detail;
      const primaryDetail = isDlaProcurementHistory(source.label) ? source.detail || DLA_USED_COMPONENT_DETAIL
        : isBtbSourceLabel(source.label) ? [BTB_EXPLANATION, btbDetail].filter(Boolean).join(' ') : source.detail;
      const supplementaryDetail = relatedLocal?.detail && relatedLocal.detail !== source.detail && !primaryDetail?.includes(relatedLocal.detail)
        ? relatedLocal.detail : null;
      return {
      label: sourceLedgerLabel(source.label), detail: [primaryDetail, supplementaryDetail].filter(Boolean).join(' '),
      status: sourceCheckStatus(source.outcome), url: isBtbSourceLabel(source.label) ? BTB_BENCHMARK_URL : undefined,
      evidenceCount: source.evidence_count, asOf: source.as_of || relatedLocal?.as_of, contribution: source.contribution,
    }; }),
    ...localChecks.map((evidence) => ({
      label: localEvidenceLabel(evidence),
      detail: evidence.detail || localEvidenceContribution(evidence),
      status: 'Used for this estimate.',
      asOf: evidence.as_of, contribution: evidence.contribution,
    })),
  ];
  const sourceChecks = sourceFacts.flatMap((fact): SourceCheck[] => sourceTarget(
    fact.label, plan, laborGroups, pricedMaterialLines, planningWorkLines, installedWorkLines, awardHasPrice,
  ).map((target) => ({ ...fact, target })));
  const sourceChecksFor = (target: string): SourceCheck[] => sourceChecks.filter((check) => check.target === target);
  const finiteAmount = (value: string | number | null | undefined): number | null => (
    value != null && value !== '' && Number.isFinite(Number(value)) ? Number(value) : null
  );
  const visibleWarnings = (plan.warnings ?? []).flatMap((warning): string[] => {
    if (ROM_DISCLOSURE_WARNINGS.has(warning) || describesUnestimatedWork(warning)
      || /\bpartial\b/i.test(warning) || SITE_WAGE_COMPARISON.test(warning)) return [];
    return [warning];
  });
  // Source records and aggregate labor totals are not the comparison base:
  // aggregate labor may include site roles and employment support. Use only
  // the displayed, priced WD craft rows, and never warn on equality.
  const cents = (value: string | number | null | undefined): number | null => {
    const amount = finiteAmount(value);
    return amount === null ? null : Math.round(amount * 100);
  };
  const craftLaborCents = (plan.labor_lines ?? []).reduce((sum, line) => sum + (cents(line.amount) ?? 0), 0);
  const siteRoleCents = cents(plan.site_role_total);
  if (craftLaborCents > 0 && siteRoleCents !== null && siteRoleCents > craftLaborCents) {
    visibleWarnings.push(SITE_WAGE_EXCEEDS_CRAFT_WARNING);
  }
  // `ApproximateCostPlanPublic` excludes the retired demo shape at the API
  // boundary. Keep this render guard as defense in depth for an old saved
  // browser fixture or a future caller that bypasses the normalizer.
  const acceptedAwardReferenceKind = plan.award_reference?.reference_kind === undefined
    || plan.award_reference?.reference_kind === 'legacy_award_range'
    || plan.award_reference?.reference_kind === 'conservative_comparable_award_point';
  const awardEligible = acceptedAwardReferenceKind && !awardEvidenceIsContextOnly && plan.award_reference?.compatible;
  const awardAmount = awardEligible && isPricedAmount(plan.award_reference?.amount)
    ? plan.award_reference?.amount : null;
  const awardLow = awardEligible ? finiteAmount(plan.award_reference?.low) : null;
  const awardHigh = awardEligible ? finiteAmount(plan.award_reference?.high) : null;
  // `priced_subtotal` is the server-owned sum of priced work only. Historical
  // fee/residual fields are intentionally absent from the public ROM DTO, so
  // an award reference never creates an implied allocation to any cost bucket.
  const modeledSubtotal = finiteAmount(plan.priced_subtotal);
  const modeledRange = plan.modeled_subtotal_range;
  const profitMargin = plan.profit_margin;
  const subtotalContributors = hasInstalledWork ? ['all-in installed work'] : [
    ...(showLabor && isPricedAmount(plan.labor_total) ? ['labor'] : []),
    ...(hasPricedDirectMaterials && isPricedAmount(displayedDirectMaterialsTotal) ? ['direct materials'] : []),
    ...(planningIndirectMaterialLines.length ? ['indirect materials'] : []),
    ...(planningOtherLines.length ? ['all-in installed work'] : []),
    ...(pricedOtherCosts.length ? ['other priced direct costs'] : []),
    ...(generalAndAdministrative ? ['G&A'] : []),
  ];
  if (profitMargin && modeledRange) subtotalContributors.push('profit');
  const subtotalBasis = subtotalContributors.length
    ? `Sum of ${listWithAnd(subtotalContributors)}.`
    : 'Sum of priced work in this result.';
  const laborDuration = plan.labor_plan_summary
    ? `About ${plan.labor_plan_summary.derived_site_days} workdays for modeled work at ${plan.labor_plan_summary.shift_hours} hours per shift.${plan.labor_plan_summary.review_state === 'needs_review' ? ' Review labor plan after input changes.' : ''}`
    : null;
  const timingInLaborBasis = !hasInstalledWork && showLabor;
  const statedProjectTiming = <StatedProjectTiming items={timingRequirements} sourceStatus={timingSourceStatus} stale={timingIsStale} />;
  const range = (low: string | number, high: string | number) => `${money(low)}–${money(high)}`;
  return <Paper component="section" variant="outlined" aria-label="Approximate cost plan (ROM)" sx={{ p: { xs: 1.5, sm: 2.5 }, mb: 2 }}>
    <Typography variant="h5" component="h2">Rough order of magnitude estimate</Typography>
    <Typography sx={{ my: 1 }}>{plan.scope_label}</Typography>
    {visibleWarnings.map((warning, i) => <Alert key={i} severity="warning" sx={{ mb: 1 }}>{warning}</Alert>)}
    <TableContainer sx={{ overflowX: 'auto' }}><Table size="small" aria-label="Approximate cost plan (ROM)" sx={{ tableLayout: 'fixed', minWidth: { xs: 520, sm: 0 }, '& td, & th': { verticalAlign: 'top', overflowWrap: 'anywhere', px: { xs: 0.75, sm: 2 } }, '& .rom-nested-label': { pl: { xs: 2.25, sm: 4 } }, '& .rom-estimate-cell': { whiteSpace: 'nowrap', overflowWrap: 'normal', fontVariantNumeric: 'tabular-nums', textAlign: 'left' }, '& .rom-estimate-cell .MuiTypography-root': { whiteSpace: 'normal' } }}>
      <colgroup>
        <col style={{ width: '38%' }} />
        <col style={{ width: '22%' }} />
        <col style={{ width: '40%' }} />
      </colgroup>
      <TableHead><TableRow><TableCell>Cost</TableCell><TableCell className="rom-estimate-cell">Estimate</TableCell><TableCell>Basis / assumptions</TableCell></TableRow></TableHead>
      <TableBody>
        {isPricedAmount(modeledSubtotal) && <TableRow sx={{ bgcolor: (theme) => alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.24 : 0.10), '& th, & td': { fontWeight: 700 } }}>
          <TableCell component="th" scope="row">Modeled subtotal</TableCell>
          <TableCell className="rom-estimate-cell">{modeledRange ? range(modeledRange.low, modeledRange.high) : money(modeledSubtotal)}</TableCell>
          <BasisCell>{subtotalBasis}{!timingInLaborBasis && statedProjectTiming}</BasisCell>
        </TableRow>}
        {!timingInLaborBasis && !isPricedAmount(modeledSubtotal) && <TableRow>
          <TableCell component="th" scope="row">Project timing</TableCell>
          <TableCell className="rom-estimate-cell">—</TableCell>
          <BasisCell>{statedProjectTiming}</BasisCell>
        </TableRow>}
        {hasInstalledWork ? <>
          <TableRow><TableCell component="th" scope="row">{expander('installed-work', `All-in installed work — ${installedWorkLines[0].label}`, 'Installed work details')}</TableCell><TableCell className="rom-estimate-cell rom-subtotal-contributor" style={subtotalContributorEstimateStyle}>{money(installedWorkLines.reduce((total, line) => total + Number(line.amount), 0))}</TableCell><BasisCell>Labor and materials combined; replaces separate component costs.{sourceChecksFor('installed-work').length > 0 && <LineItemDetails label="All-in installed work"><SourceChecks checks={sourceChecksFor('installed-work')} /></LineItemDetails>}</BasisCell></TableRow>
          {detail('installed-work', <InstalledWorkDetails lines={installedWorkLines} />, 'Installed work details')}
        </> : <>
          {showLabor && <><TableRow>
            <TableCell component="th" scope="row">{expander('labor', 'Labor', 'Labor details')}</TableCell>
            <TableCell className="rom-estimate-cell rom-subtotal-contributor" style={subtotalContributorEstimateStyle}>{money(plan.labor_total)}</TableCell>
            <BasisCell>{[wageRefreshFailed && pricedLaborLines.length > 0 ? 'Used saved Wage Determination after refresh failed.' : null, laborDuration].filter(Boolean).join(' ')}{statedProjectTiming}</BasisCell>
          </TableRow>
          {expanded.labor && <><CompactLineItems kind="labor" laborLines={pricedLaborLines} siteRoleLines={pricedSiteRoleLines} indirectAllowances={indirectAllowances} sourceAdjustments={plan.source_adjustments} sourceChecksFor={sourceChecksFor} />{unpricedSiteRoles.map((row) => <UnpricedSiteRoleRow key={row.role_key} row={row} />)}</>}</>}
          {showDirectMaterials && <><TableRow>
            <TableCell component="th" scope="row">{expander('direct-materials', 'Direct materials', 'Direct materials details')}</TableCell>
            <TableCell className="rom-estimate-cell rom-subtotal-contributor" style={subtotalContributorEstimateStyle}>{hasPricedDirectMaterials ? money(displayedDirectMaterialsTotal) : unpricedDirectMaterialComponents.length > 0 ? 'Not estimated' : '—'}</TableCell>
            <BasisCell>{plan.assumptions?.some(assumption => assumption.startsWith('Drywall specifications are planning assumptions, not SOW facts:')) && <LineItemDetails label="Direct materials"><Typography variant="body2">Planning assumption: ½-inch board, wood framing, Level 4 finish; not specified in the SOW.</Typography></LineItemDetails>}</BasisCell>
          </TableRow>
          {expanded['direct-materials'] && <><CompactLineItems kind="materials" materialLines={pricedMaterialLines} sourceAdjustments={plan.source_adjustments} sourceChecksFor={sourceChecksFor} />{planningDirectMaterialLines.map((line) => <PlanningWorkLineRow key={line.occurrence_id} line={line} nested sourceChecks={sourceChecksFor(line.occurrence_id)} />)}{unpricedDirectMaterialComponents.map((row) => <UnpricedExplicitRow key={row.occurrence_id} row={row} nested />)}</>}</>}
          {showIndirectMaterials && <><TableRow>
            <TableCell component="th" scope="row">{expander('indirect-materials', 'Indirect materials', 'Indirect materials details')}</TableCell>
            <TableCell className="rom-estimate-cell rom-subtotal-contributor" style={subtotalContributorEstimateStyle}>{money(planningIndirectMaterialLines.length ? planningIndirectMaterialTotal : null)}</TableCell>
            <BasisCell />
          </TableRow>
          {expanded['indirect-materials'] && <>{planningIndirectMaterialLines.map((line) => <PlanningWorkLineRow key={line.occurrence_id} line={line} nested sourceChecks={sourceChecksFor(line.occurrence_id)} />)}{unpricedIndirectMaterialComponents.map((row) => <UnpricedExplicitRow key={row.occurrence_id} row={row} nested />)}</>}</>}
          {planningOtherLines.map((line) => <PlanningWorkLineRow key={line.occurrence_id} line={line} sourceChecks={sourceChecksFor(line.occurrence_id)} />)}
          {pricedOtherCosts.map((row, i) => <TableRow key={`priced-other-${i}`}><TableCell component="th" scope="row">{row.label}</TableCell><TableCell className="rom-estimate-cell rom-subtotal-contributor" style={subtotalContributorEstimateStyle}>{money(row.amount)}</TableCell><BasisCell>{row.basis}</BasisCell></TableRow>)}
          {generalAndAdministrative && <TableRow><TableCell component="th" scope="row">{generalAndAdministrative.label}</TableCell><TableCell className="rom-estimate-cell rom-subtotal-contributor" style={subtotalContributorEstimateStyle}>{money(generalAndAdministrative.amount)}</TableCell><BasisCell>{conciseGAndABasis(generalAndAdministrative.basis)}<LineItemDetails label={generalAndAdministrative.label}>
            {Number.isFinite(Number(generalAndAdministrative.percentage)) && isPricedAmount(generalAndAdministrative.base_amount)
              ? <Typography variant="body2">{Number(generalAndAdministrative.percentage) * 100}% × {money(generalAndAdministrative.base_amount)} = {money(generalAndAdministrative.amount)}</Typography>
              : <Typography variant="body2">Calculation inputs are unavailable in this saved result.</Typography>}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Planning allowance on verified Wage Determination labor, labor support, recipe materials, and separately priced direct project costs. Site-role wages, unpriced work, and G&A itself are outside this base; it is not a supplier quote or profit.</Typography>
          </LineItemDetails></BasisCell></TableRow>}
        </>}
        {profitMargin && modeledRange && <TableRow>
          <TableCell component="th" scope="row">Profit margin</TableCell>
          <TableCell className="rom-estimate-cell rom-subtotal-contributor" style={subtotalContributorEstimateStyle}>{range(profitMargin.low, profitMargin.high)}</TableCell>
          <BasisCell>Planning assumption: 7–12% of priced labor and direct/indirect materials ({money(profitMargin.base_amount)}).</BasisCell>
        </TableRow>}
        <TableRow sx={{ bgcolor: 'action.selected', '& th, & td': { fontWeight: 700 } }}>
          <TableCell component="th" scope="row">Award-based planning total</TableCell>
          <TableCell className="rom-estimate-cell">{awardLow !== null && awardHigh !== null ? range(awardLow, awardHigh) : awardAmount != null ? `${money(awardAmount)} (point estimate; range unavailable)` : 'Not estimated'}</TableCell>
          <BasisCell>{awardLow !== null && awardHigh !== null
            ? <>{plan.award_reference?.basis_label} {AWARD_RANGE_SCOPE_DISCLOSURE}</>
            : awardAmount != null ? <>
              {plan.award_reference?.basis_label ? `${plan.award_reference.basis_label} ` : ''}
              This saved award has a point estimate only.
            </>
              : awardEvidenceIsContextOnly ? 'Context-only award evidence is not used as a planning total.'
                : awardReferenceUnavailableReason || 'No compatible award total is available.'}{sourceChecksFor('award').length > 0 && <LineItemDetails label="Award-based planning total"><Typography variant="body2">Previous federal awards are whole-project totals, not item prices.</Typography><SourceChecks checks={sourceChecksFor('award')} /></LineItemDetails>}</BasisCell>
        </TableRow>
        {supportingEvidence.map((evidence, index) => {
          const id = `support-${index}`;
          return <React.Fragment key={`${evidence.id}-${index}`}>
            <TableRow><TableCell colSpan={3}>{expander(id, evidence.label, evidence.label)}</TableCell></TableRow>
            {detail(id, evidence.content, evidence.label)}
          </React.Fragment>;
        })}
        {awardEvidence && <>
          <TableRow><TableCell colSpan={3}>{expander('awards', 'Previous federal awards — USAspending', 'Previous federal awards evidence')}</TableCell></TableRow>
          {detail('awards', <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              USAspending records are reviewed historical whole-award totals. They are not itemized prices, unit rates, or a labor and materials breakdown.
            </Typography>
            {awardEvidence}
          </Box>, 'Previous federal awards evidence')}
        </>}
      </TableBody>
    </Table></TableContainer>
  </Paper>;
}
